import { ChevronRight } from 'lucide-react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { memo, useMemo } from 'react'

import { CryptoIcon } from '@/components/widget/crypto-icon'
import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { MarketStatusBadge } from '@/components/widget/market-status-badge'
import type { CappedWizardAsset } from '@/features/dashboard/components/watchlist-wizard/asset-grouping'
import { WatchlistGroupSelectButton } from '@/features/dashboard/components/watchlist-wizard/watchlist-group-select-button'
import {
  formatPrice,
  selectableAssetInstruments,
  wizardGroupKey,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-grouping'
import type { LiveWatchlistTicker } from '@/features/dashboard/hooks/use-watchlist-live-ticker'
import { highlightMatch } from '@/lib/highlight-match'
import { cn, safeParseFloat } from '@/lib/utils'
import { displayPriceOf } from '@/lib/utils/display-price'
import type { Exchange, Instrument } from '@/types/generated/common/types'

// Fixed row height, declared once and used by both the row and the section
// decorations so the virtual list never has to measure a row to know where
// the next one starts.
export const MASTER_ROW_HEIGHT_PX = 50

// How many venue logos show before collapsing the rest into a plain "+N" count —
// three is the most this row's width can fit without crowding the price.
const MAX_VISIBLE_EXCHANGE_ICONS = 3

interface WatchlistMasterRowProps {
  asset: CappedWizardAsset
  index: number
  /** Ticker — the thing the user typed. */
  label: string
  /** Human name plus asset class, e.g. "Bitcoin · Crypto". */
  description: string
  /** Distinct venues this asset trades on, in a fixed display order. */
  exchangeIconKeys: string[]
  /** Looked up by the parent from a single batched, visible-range-scoped
   *  subscription (see `useWatchlistLiveTickers`) rather than this row
   *  subscribing on its own — undefined when this asset's representative
   *  instrument isn't in the currently-tracked range. */
  ticker: LiveWatchlistTicker | undefined
  /** Exchange backing the displayed representative ticker price. */
  marketExchange: Exchange | undefined
  selected: boolean
  singleMode: boolean
  searching: boolean
  query: string
  showCollapsedOptionChain: boolean
  existingKeys: Set<string>
  selectedKeys: Set<string>
  selectAllLabel: string
  deselectAllLabel: string
  onToggleGroup: (instruments: Instrument[]) => void
  onHover: (index: number, event: ReactMouseEvent<HTMLElement>) => void
  onActivate: (index: number) => void
}

export const WatchlistMasterRow = memo(function WatchlistMasterRow({
  asset,
  index,
  label,
  description,
  exchangeIconKeys,
  ticker,
  marketExchange,
  selected,
  singleMode,
  searching,
  query,
  showCollapsedOptionChain,
  existingKeys,
  selectedKeys,
  selectAllLabel,
  deselectAllLabel,
  onToggleGroup,
  onHover,
  onActivate,
}: WatchlistMasterRowProps) {
  const baseCurrency = asset.instruments[0]?.baseCurrency ?? asset.key
  const rawPrice = displayPriceOf(ticker)
  const price = formatPrice(rawPrice)
  const changePercent = ticker?.priceChangePercent24h
    ? safeParseFloat(ticker.priceChangePercent24h)
    : undefined
  const hasChange = changePercent !== undefined && Number.isFinite(changePercent)
  const visibleExchangeIcons = exchangeIconKeys.slice(0, MAX_VISIBLE_EXCHANGE_ICONS)
  const hiddenExchangeCount = exchangeIconKeys.length - visibleExchangeIcons.length
  const selectableInstruments = useMemo(
    () =>
      showCollapsedOptionChain
        ? selectableAssetInstruments(
            asset.instruments,
            rawPrice ? safeParseFloat(rawPrice) : undefined
          )
        : asset.instruments,
    [asset.instruments, rawPrice, showCollapsedOptionChain]
  )

  return (
    <div
      className={cn('flex items-center', selected && 'bg-foreground/6')}
      style={{ height: MASTER_ROW_HEIGHT_PX }}
      onMouseEnter={(event) => onHover(index, event)}
    >
      {!singleMode ? (
        <div className="pl-3">
          <WatchlistGroupSelectButton
            instruments={selectableInstruments}
            label={asset.key}
            size="sm"
            existingKeys={existingKeys}
            selectedKeys={selectedKeys}
            selectAllLabel={selectAllLabel}
            deselectAllLabel={deselectAllLabel}
            onToggle={onToggleGroup}
          />
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => onActivate(index)}
        data-keyboard-row-key={wizardGroupKey(asset)}
        className={cn(
          'group flex h-full min-w-0 flex-1 items-center gap-[9px] pr-2 pl-4 text-left'
        )}
        aria-current={selected ? 'true' : undefined}
      >
        {asset.key === 'HYPE' ? (
          <ExchangeIcon exchange="hyperliquid" className="size-7 shrink-0 rounded-full" />
        ) : (
          <CryptoIcon
            symbol={baseCurrency}
            iconTag={
              asset.instruments.find((item) => item.enrichment?.iconTag)?.enrichment?.iconTag
            }
            className="size-7 shrink-0 rounded-full"
            fallback={
              <span className="flex size-full items-center justify-center bg-white/[0.07] text-[10px] font-semibold">
                {label.slice(0, 3)}
              </span>
            }
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm leading-tight font-semibold">
            {searching ? highlightMatch(label, query) : label}
          </span>
          <span className="text-foreground/40 block truncate text-xs leading-tight">
            {searching ? highlightMatch(description, query) : description}
          </span>
        </span>
        {visibleExchangeIcons.length > 0 ? (
          <span className="flex shrink-0 items-center">
            {visibleExchangeIcons.map((iconKey, iconIndex) => (
              <ExchangeIcon
                key={iconKey}
                exchange={iconKey}
                className={cn(
                  'ring-popover size-4 shrink-0 rounded-full ring-2',
                  iconIndex > 0 && '-ml-1.5'
                )}
              />
            ))}
            {hiddenExchangeCount > 0 ? (
              <span className="text-foreground/50 ml-1.5 shrink-0 text-[11px] leading-none font-medium tabular-nums">
                +{hiddenExchangeCount}
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="shrink-0 text-right">
          {price ? (
            <span className="block text-sm leading-tight font-medium tabular-nums">{price}</span>
          ) : null}
          {hasChange ? (
            <span
              className={cn(
                'block text-xs leading-tight tabular-nums',
                changePercent >= 0 ? 'text-chart-mint' : 'text-chart-red'
              )}
            >
              {changePercent >= 0 ? '↑' : '↓'} {Math.abs(changePercent).toFixed(2)}%
            </span>
          ) : null}
        </span>
        <MarketStatusBadge exchange={marketExchange} className="size-3.5 shrink-0" />
        <ChevronRight className="text-foreground/30 size-4 shrink-0 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
      </button>
    </div>
  )
})

interface WatchlistSectionHeaderProps {
  label: string
}

export function WatchlistSectionHeader({ label }: WatchlistSectionHeaderProps) {
  return (
    <div className="flex items-center gap-3 px-4 pt-4 pb-1.5">
      <span className="text-foreground/30 text-[10px] font-semibold tracking-tight uppercase">
        {label}
      </span>
      <span className="bg-foreground/7 h-px flex-1" />
    </div>
  )
}

interface WatchlistSeeAllRowProps {
  label: string
  onClick: () => void
}

export function WatchlistSeeAllRow({ label, onClick }: WatchlistSeeAllRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-foreground/45 hover:text-foreground flex w-full items-center gap-1 px-4 py-2.5 text-left text-xs font-medium transition-colors'
      )}
    >
      {label}
      <ChevronRight className="size-3.5" aria-hidden="true" />
    </button>
  )
}
